import ApplicationFilters from '@/components/DashboardFilters/ApplicationFilters';
import DashboardLayout from '@/components/layouts/DashboardLayout';
import {
   Table,
   TableBody,
   TableHead,
   TableHeader,
   TableRow,
} from "@/components/ui/table";
import { useMediaQuery } from '@/hooks/useMediaQuery';
import useTabNavigation from '@/hooks/useTabNavigation';
import useTourGuide from '@/hooks/useTourGuide';
import { useTypewriter } from '@/hooks/useTypewriter';
import { gql, useQuery } from '@apollo/client';
import { FileX } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useApplications } from '../../../hooks/useApplications';
import RRC_ApplicationRow from './RRC_ApplicationRow';

const GET_CERTIFICATES = gql`
   query GetCertificates($status: String) {
      getCertificates(status: $status) {
         id
         certificateNumber
         applicationId
         applicationType
         certificateStatus
         dateCreated
         dateIssued
         expiryDate
         dateApproved
         forestRangerSignature {
            data
            contentType
         }
         chiefTSDSignature {
            data
            contentType
         }
         penrCenrOfficerSignature {
            data
            contentType
         }
         csawCertificateData {
            registrationType
            ownerName
            address
            chainsawDetails {
               brand
               model
               serialNumber
               dateOfAcquisition
               powerOutput
               maxLengthGuidebar
               countryOfOrigin
               purchasePrice
            }
            purpose
            otherDetails
         }
         covCertificateData {
            applicationNumber
            name
            driverName
            driverAddress
            originAddress
            destinationAddress
            forestProducts {
               kind
               species
               scientificName
               numberOfTrees
               quantity {
                  value
                  unit
               }
               volume {
                  cubicMeters
                  boardFeet
               }
            }
            conveyance
            vehiclePlateNumber
            consignee
            permitNumber
         }
         orderOfPayment {
            id
            OOPstatus
            officialReceipt {
               orNumber
               dateIssued
               amount
               paymentMethod
               remarks
            }
         }
      }
   }
`;

const ReceivingReleasingClerkDashboard = () => {
   const navigate = useNavigate();
   const location = useLocation();


   const [filters, setFilters] = useState({
      searchTerm: '',
      applicationType: '',
      dateRange: {
         from: undefined,
         to: undefined
      }
   });

   const mainTabs = useMemo(() => ['Pending Release', 'Released Certificates'], []);

   // Initialize tab navigation
   const {
      activeMainTab,
      handleMainTabChange: handleTabChange
   } = useTabNavigation({
      mainTabs,
      defaultMainTab: 'Pending Release'
   });

   // Use useMemo for query parameters
   const queryParams = useMemo(() => {
      switch (activeMainTab) {
         case 'Pending Release':
            return {
               hasCertificate: true,
               currentStage: 'PendingRelease',
               status: 'In Progress',
               certificateSignedByPENRCENROfficer: true
            };
         case 'Released Certificates':
            return {
               hasCertificate: true,
               currentStage: 'Released',
               certificateSignedByPENRCENROfficer: true
            };
         default:
            return { currentStage: 'ForRecordByReceivingClerk' };
      }
   }, [activeMainTab]);

   const { applications, loading: appLoading, error: appError, refetch: refetchApps } = useApplications(queryParams);
   const { applications: allApplications, refetch: refetchAllApplications } = useApplications({});

   // Add the certificates query
   const { data: certificatesData, loading: certificatesLoading, error: certificatesError, refetch: refetchCertificates }
      = useQuery(GET_CERTIFICATES, {
         variables: {
            status: activeMainTab === 'Pending Release' ? 'Pending Release' : 'Released'
         },
         skip: !activeMainTab.includes('Certificates'),
      });

   // Define handleRefetch
   const handleRefetch = useCallback(() => {
      refetchApps();
      refetchCertificates();
      refetchAllApplications();
   }, [refetchApps, refetchCertificates, refetchAllApplications]);

   useEffect(() => {
      handleRefetch();
   }, [handleRefetch, activeMainTab]);

   useEffect(() => {
      const pollInterval = setInterval(handleRefetch, 30000);
      return () => clearInterval(pollInterval);
   }, [handleRefetch]);

   // const handleSubTabChange = (tab) => {
   //    // setActiveSubTab(tab);
   //    setActiveMainTab(tab);
   //    setFilters({
   //       searchTerm: '',
   //       applicationType: '',
   //       dateRange: { from: undefined, to: undefined }
   //    });
   //    handleRefetch();
   // };

   const filteredApplications = useMemo(() => {
      return applications.filter(app => {
         const matchesSearch = app.applicationNumber.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
            app.applicationType.toLowerCase().includes(filters.searchTerm.toLowerCase());

         const matchesType = !filters.applicationType ||
            filters.applicationType === "all" ||
            app.applicationType === filters.applicationType;

         const matchesDateRange = (() => {
            if (!filters.dateRange.from && !filters.dateRange.to) return true;
            const appDate = new Date(app.dateOfSubmission);
            appDate.setHours(0, 0, 0, 0);
            const fromDate = filters.dateRange.from ? new Date(filters.dateRange.from) : null;
            const toDate = filters.dateRange.to ? new Date(filters.dateRange.to) : null;
            if (fromDate) fromDate.setHours(0, 0, 0, 0);
            if (toDate) toDate.setHours(0, 0, 0, 0);
            return (!fromDate || appDate >= fromDate) && (!toDate || appDate <= toDate);
         })();

         return matchesSearch && matchesType && matchesDateRange;
      });
   }, [applications, filters]);

   const getStatusColor = (status) => {
      switch (status.toLowerCase()) {
         case 'pending': return 'bg-yellow-100 text-yellow-800';
         case 'recorded': return 'bg-blue-100 text-blue-800';
         case 'pending release': return 'bg-orange-100 text-orange-800';
         case 'released': return 'bg-green-100 text-green-800';
         default: return 'bg-gray-100 text-gray-800';
      }
   };

   const isMobile = useMediaQuery('(max-width: 640px)');

   const descriptions = useMemo(() => ({
      'Pending Release': 'Certificates ready for release',
      'Released Certificates': 'Certificates released to applicants'
   }), []);

   const descriptionText = useTypewriter(descriptions[activeMainTab] || '');

   const renderTabDescription = () => (
      <div className="mb-4 -mt-4">
         <h1 className="text-sm min-h-[20px] text-black dark:text-gray-300">{descriptionText}</h1>
      </div>
   );

   const renderContent = () => {
      if (appLoading) {
         return <div className="flex justify-center py-8">Loading applications...</div>;
      }

      if (appError) {
         return <div className="text-destructive text-center py-8">Error loading applications</div>;
      }

      if (filteredApplications.length === 0) {
         return (
            <div className="text-center py-8">
               <FileX className="mx-auto h-12 w-12 text-muted-foreground" />
               <h3 className="mt-2 text-lg font-semibold">No applications found</h3>
               <p className="text-sm text-muted-foreground">
                  {filters.applicationType ?
                     `No applications found for ${filters.applicationType}` :
                     'No applications available'}
               </p>
            </div>
         );
      }

      if (isMobile) {
         return (
            <div className="space-y-4">
               {filteredApplications.map((app) => (
                  <RRC_ApplicationRow
                     key={app.id}
                     app={app}
                     onRecordComplete={handleRefetch}
                     getStatusColor={getStatusColor}
                     currentTab={activeMainTab}
                     isMobile={true}
                  />
               ))}
            </div>
         );
      }

      return (
         <Table>
            <TableHeader>
               <TableRow>
                  <TableHead>Application Number</TableHead>
                  <TableHead>Application Type</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
               </TableRow>
            </TableHeader>
            <TableBody>
               {filteredApplications.map((app) => (
                  <RRC_ApplicationRow
                     key={app.id}
                     app={app}
                     onRecordComplete={handleRefetch}
                     getStatusColor={getStatusColor}
                     currentTab={activeMainTab}
                     isMobile={false}
                  />
               ))}
            </TableBody>
         </Table>
      );
   };

   const getTabCounts = useMemo(() => {
      if (!allApplications) return {};

      return {
         'Pending Release': allApplications.filter(app =>
            app.hasCertificate &&
            app.currentStage === 'PendingRelease' &&
            app.status === 'In Progress' &&
            app.certificateSignedByPENRCENROfficer
         ).length,
         'Released Certificates': allApplications.filter(app =>
            app.hasCertificate &&
            app.currentStage === 'Released' &&
            app.certificateSignedByPENRCENROfficer
         ).length
      };
   }, [allApplications]);

   const { startTour: initiateTour } = useTourGuide({
      tourId: 'receiving-releasing-clerk-tour',
      onFinish: () => {
         console.log('Tour finished');
      },
   });

   const receivingReleasingClerkTourSteps = useMemo(() => {
      // Base steps that are always available
      const baseSteps = [
         {
            target: '.main-tabs',
            content: 'Switch between different application stages using these tabs',
            placement: 'bottom',
            disableBeacon: true,
         },
         {
            target: '.applications-filters',
            content: 'Filter applications by type and date range',
            placement: 'bottom',
         }
      ];

      // Add table steps based on whether we have data or not
      if (filteredApplications?.length > 0) {
         baseSteps.push(
            {
               target: '.applications-table',
               content: 'View all applications that need your attention',
               placement: 'top',
            },
            {
               target: '.table-actions',
               content: 'Here you can find different actions for each application',
               placement: 'left',
            },
            {
               target: '.view-action',
               content: 'Click here to view the complete details of the application',
               placement: 'left',
            }
         );

         // Add specific steps based on the current tab
         if (activeMainTab === 'Pending Release') {
            baseSteps.push(
               {
                  target: '.release-action',
                  content: 'Click here to release the permit/certificate to the applicant',
                  placement: 'left',
               },
               {
                  target: '.view-certificate-action',
                  content: 'Click here to view the permit/certificate',
                  placement: 'left',
               }
            );
         } else if (activeMainTab === 'Released Certificates') {
            baseSteps.push({
               target: '.undo-action',
               content: 'Click here to undo the release if needed',
               placement: 'left',
            });
         }
      } else {
         baseSteps.push({
            target: '.applications-table',
            content: 'Applications will appear here once they need your attention',
            placement: 'top',
         });
      }

      return baseSteps;
   }, [activeMainTab, filteredApplications?.length]);

   // Add useEffect for tour initialization
   useEffect(() => {
      const tourButton = document.querySelector('.help-tour-button');
      if (tourButton) {
         tourButton.disabled = appLoading;
      }
   }, [appLoading]);

   const handleStartTour = useCallback(async () => {
      // Wait for data to be loaded
      if (appLoading) {
         toast.info('Please wait while data is loading...');
         return;
      }

      // Start the tour
      initiateTour(receivingReleasingClerkTourSteps);
   }, [appLoading, initiateTour, receivingReleasingClerkTourSteps]);

   return (
      <DashboardLayout
         title="Receiving/Releasing Clerk Dashboard"
         description="Manage and process applications and certificates"
         onRefresh={handleRefetch}
         isMobile={isMobile}
         mainTabs={mainTabs}
         activeMainTab={activeMainTab}
         onMainTabChange={handleTabChange}
         tabDescription={renderTabDescription()}
         filters={<ApplicationFilters filters={filters} setFilters={setFilters} />}
         helpTour={handleStartTour}
         tabCounts={getTabCounts}
      >
         {renderContent()}
      </DashboardLayout>
   );
};

export default ReceivingReleasingClerkDashboard;
